import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  deletePageSeo,
  getAdminSeoSettings,
  regenerateSitemap,
  saveGlobalSeo,
  savePageSeo,
  saveRobotsSettings,
} from '../services/adminSeoService';
import '../styles/adminSeo.css';

const emptyPage = {
  pageKey: '',
  label: '',
  path: '/',
  metaTitle: '',
  metaDescription: '',
  metaKeywords: '',
  openGraphTitle: '',
  openGraphDescription: '',
  openGraphImageUrl: '',
  canonicalUrl: '',
  robots: 'index, follow',
  sitemapPriority: 0.7,
  changeFrequency: 'monthly',
  sourceType: 'custom',
  isManaged: true,
};

const changeFrequencies = ['always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never'];
const robotsOptions = ['index, follow', 'index, nofollow', 'noindex, follow', 'noindex, nofollow'];
const twitterCardTypes = ['summary_large_image', 'summary'];

function toLines(values = []) {
  return (values || []).join('\n');
}

function fromLines(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : 'Not generated yet';
}

function buildRobotsPreview(robots) {
  const allowLines = fromLines(robots.allowPathsText).map((path) => `Allow: ${path}`);
  const disallowLines = fromLines(robots.disallowPathsText).map((path) => `Disallow: ${path}`);
  return ['User-agent: *', ...allowLines, ...disallowLines, '', `Sitemap: ${robots.sitemapUrl || ''}`].join('\n');
}

function Field({ children, helper, label }) {
  return (
    <label>
      {label}
      {children}
      {helper ? <span className="admin-label-help">{helper}</span> : null}
    </label>
  );
}

function AdminSeo() {
  const [settings, setSettings] = useState(null);
  const [globalForm, setGlobalForm] = useState({});
  const [pageForm, setPageForm] = useState(emptyPage);
  const [robotsForm, setRobotsForm] = useState({ allowPathsText: '/', disallowPathsText: '/admin\n/api', sitemapUrl: '' });
  const [selectedPageKey, setSelectedPageKey] = useState('');
  const [status, setStatus] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  const pages = useMemo(() => settings?.pages || [], [settings]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminSeoSettings();
      const nextSettings = data.settings;
      setSettings(nextSettings);
      setGlobalForm(nextSettings.global || {});
      setRobotsForm({
        allowPathsText: toLines(nextSettings.robots?.allowPaths || ['/']),
        disallowPathsText: toLines(nextSettings.robots?.disallowPaths || ['/admin', '/api']),
        sitemapUrl: nextSettings.robots?.sitemapUrl || 'https://factorstreet.com/sitemap.xml',
      });
      setSelectedPageKey((current) => current || nextSettings.pages?.[0]?.pageKey || '');
      setStatus({ type: 'success', text: 'SEO settings loaded.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Unable to load SEO settings.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const nextPage = pages.find((page) => page.pageKey === selectedPageKey);

    if (nextPage) {
      setPageForm(nextPage);
    } else if (!selectedPageKey && pages[0]) {
      setSelectedPageKey(pages[0].pageKey);
      setPageForm(pages[0]);
    }
  }, [pages, selectedPageKey]);

  const updateGlobal = (field, value) => setGlobalForm((current) => ({ ...current, [field]: value }));
  const updatePage = (field, value) => setPageForm((current) => ({ ...current, [field]: value }));
  const updateRobots = (field, value) => setRobotsForm((current) => ({ ...current, [field]: value }));

  const applySettings = (nextSettings) => {
    setSettings(nextSettings);
    setGlobalForm(nextSettings.global || {});
    setRobotsForm({
      allowPathsText: toLines(nextSettings.robots?.allowPaths || ['/']),
      disallowPathsText: toLines(nextSettings.robots?.disallowPaths || ['/admin', '/api']),
      sitemapUrl: nextSettings.robots?.sitemapUrl || 'https://factorstreet.com/sitemap.xml',
    });
  };

  const handleSaveGlobal = async () => {
    setSaving('global');
    try {
      const data = await saveGlobalSeo(globalForm);
      applySettings(data.settings);
      setStatus({ type: 'success', text: 'Global SEO settings saved.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Unable to save global SEO.' });
    } finally {
      setSaving('');
    }
  };

  const handleSavePage = async () => {
    setSaving('page');
    try {
      const data = await savePageSeo(pageForm);
      applySettings(data.settings);
      setSelectedPageKey(pageForm.pageKey);
      setStatus({ type: 'success', text: 'Page SEO saved.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Unable to save page SEO.' });
    } finally {
      setSaving('');
    }
  };

  const handleAddPage = () => {
    const pageKey = `custom-page-${Date.now()}`;
    setSelectedPageKey(pageKey);
    setPageForm({
      ...emptyPage,
      pageKey,
      label: 'Custom Page',
      path: '/custom-page',
      canonicalUrl: 'https://factorstreet.com/custom-page',
    });
    setStatus({ type: 'success', text: 'Draft custom SEO page ready. Edit fields and save it.' });
  };

  const handleDeletePage = async () => {
    if (!pageForm.pageKey) {
      return;
    }

    setSaving('delete');
    try {
      const data = await deletePageSeo(pageForm.pageKey);
      applySettings(data.settings);
      setSelectedPageKey(data.settings.pages?.[0]?.pageKey || '');
      setStatus({ type: 'success', text: 'Selected SEO override deleted or reset to defaults.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Unable to delete page SEO.' });
    } finally {
      setSaving('');
    }
  };

  const handleSaveRobots = async () => {
    setSaving('robots');
    try {
      const data = await saveRobotsSettings({
        allowPaths: fromLines(robotsForm.allowPathsText),
        disallowPaths: fromLines(robotsForm.disallowPathsText),
        sitemapUrl: robotsForm.sitemapUrl,
      });
      applySettings(data.settings);
      setStatus({ type: 'success', text: 'Robots settings saved.' });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Unable to save robots settings.' });
    } finally {
      setSaving('');
    }
  };

  const handleRegenerateSitemap = async () => {
    setSaving('sitemap');
    try {
      const data = await regenerateSitemap();
      applySettings(data.settings);
      setStatus({ type: 'success', text: `Sitemap stats regenerated with ${data.urlCount} URLs.` });
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Unable to regenerate sitemap.' });
    } finally {
      setSaving('');
    }
  };

  const previewTitle = pageForm.metaTitle || pageForm.openGraphTitle || globalForm.siteName || 'FactorStreet';
  const previewDescription = pageForm.metaDescription || globalForm.defaultMetaDescription || '';
  const previewUrl = pageForm.canonicalUrl || `${globalForm.siteUrl || 'https://factorstreet.com'}${pageForm.path || '/'}`;

  return (
    <section className="admin-page admin-seo-page">
      <div className="admin-page__header admin-page__header--split">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>SEO MANAGEMENT</h1>
          <p>Manage public metadata, crawler files, and page-level search previews.</p>
        </div>
        <button className="btn btn--secondary" disabled={loading} onClick={loadSettings} type="button">
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {status.text ? <p className={`form-alert form-alert--${status.type}`}>{status.text}</p> : null}
      {loading ? <p className="admin-empty">Loading SEO settings...</p> : null}

      <article className="admin-panel admin-seo-panel">
        <div className="admin-seo-section-heading">
          <p className="eyebrow">Global SEO Settings</p>
          <h2>Website defaults</h2>
        </div>
        <div className="admin-seo-grid">
          <Field label="Site name"><input value={globalForm.siteName || ''} onChange={(event) => updateGlobal('siteName', event.target.value)} /></Field>
          <Field label="Site URL"><input value={globalForm.siteUrl || ''} onChange={(event) => updateGlobal('siteUrl', event.target.value)} /></Field>
          <Field label="Default meta title format"><input value={globalForm.defaultMetaTitleFormat || ''} onChange={(event) => updateGlobal('defaultMetaTitleFormat', event.target.value)} /></Field>
          <Field label="Default Twitter/X card type">
            <select value={globalForm.defaultTwitterCardType || 'summary_large_image'} onChange={(event) => updateGlobal('defaultTwitterCardType', event.target.value)}>
              {twitterCardTypes.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </Field>
          <Field label="Default meta description"><textarea value={globalForm.defaultMetaDescription || ''} onChange={(event) => updateGlobal('defaultMetaDescription', event.target.value)} /></Field>
          <Field label="Default meta keywords"><textarea value={globalForm.defaultMetaKeywords || ''} onChange={(event) => updateGlobal('defaultMetaKeywords', event.target.value)} /></Field>
          <Field label="Default Open Graph image URL" helper="Leave empty until a real public image exists.">
            <input value={globalForm.defaultOpenGraphImageUrl || ''} onChange={(event) => updateGlobal('defaultOpenGraphImageUrl', event.target.value)} />
          </Field>
          <Field label="Global robots default">
            <select value={globalForm.globalRobotsDefault || 'index, follow'} onChange={(event) => updateGlobal('globalRobotsDefault', event.target.value)}>
              {robotsOptions.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </Field>
        </div>
        <button className="btn btn--primary" disabled={saving === 'global'} onClick={handleSaveGlobal} type="button">
          {saving === 'global' ? 'Saving...' : 'Save Global SEO'}
        </button>
      </article>

      <article className="admin-panel admin-seo-panel">
        <div className="admin-seo-section-heading admin-seo-section-heading--split">
          <div>
            <p className="eyebrow">Page-Level SEO Manager</p>
            <h2>Public page metadata</h2>
          </div>
          <button className="btn btn--secondary" onClick={handleAddPage} type="button">Add Page</button>
        </div>

        <div className="admin-seo-page-layout">
          <div className="admin-seo-editor">
            <Field label="Select page">
              <select value={selectedPageKey} onChange={(event) => setSelectedPageKey(event.target.value)}>
                {pages.map((page) => <option key={page.pageKey} value={page.pageKey}>{page.label} ({page.path})</option>)}
                {pageForm.pageKey && !pages.some((page) => page.pageKey === pageForm.pageKey) ? (
                  <option value={pageForm.pageKey}>{pageForm.label} ({pageForm.path})</option>
                ) : null}
              </select>
            </Field>
            <div className="admin-seo-grid">
              <Field label="Page key"><input value={pageForm.pageKey || ''} onChange={(event) => updatePage('pageKey', event.target.value)} /></Field>
              <Field label="Page label"><input value={pageForm.label || ''} onChange={(event) => updatePage('label', event.target.value)} /></Field>
              <Field label="URL slug/path"><input value={pageForm.path || ''} onChange={(event) => updatePage('path', event.target.value)} /></Field>
              <Field label={`Meta description (${(pageForm.metaDescription || '').length}/160)`}>
                <textarea value={pageForm.metaDescription || ''} onChange={(event) => updatePage('metaDescription', event.target.value)} />
              </Field>
              <Field label="Meta title"><input value={pageForm.metaTitle || ''} onChange={(event) => updatePage('metaTitle', event.target.value)} /></Field>
              <Field label="Meta keywords"><textarea value={pageForm.metaKeywords || ''} onChange={(event) => updatePage('metaKeywords', event.target.value)} /></Field>
              <Field label="Open Graph title"><input value={pageForm.openGraphTitle || ''} onChange={(event) => updatePage('openGraphTitle', event.target.value)} /></Field>
              <Field label="Open Graph description"><textarea value={pageForm.openGraphDescription || ''} onChange={(event) => updatePage('openGraphDescription', event.target.value)} /></Field>
              <Field label="Open Graph image URL"><input value={pageForm.openGraphImageUrl || ''} onChange={(event) => updatePage('openGraphImageUrl', event.target.value)} /></Field>
              <Field label="Canonical URL"><input value={pageForm.canonicalUrl || ''} onChange={(event) => updatePage('canonicalUrl', event.target.value)} /></Field>
              <Field label="Robots tag">
                <select value={pageForm.robots || 'index, follow'} onChange={(event) => updatePage('robots', event.target.value)}>
                  {robotsOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </Field>
              <Field label="Sitemap priority">
                <input max="1" min="0" step="0.05" type="number" value={pageForm.sitemapPriority ?? 0.7} onChange={(event) => updatePage('sitemapPriority', event.target.value)} />
              </Field>
              <Field label="Change frequency">
                <select value={pageForm.changeFrequency || 'monthly'} onChange={(event) => updatePage('changeFrequency', event.target.value)}>
                  {changeFrequencies.map((frequency) => <option key={frequency} value={frequency}>{frequency}</option>)}
                </select>
              </Field>
            </div>
            <div className="admin-seo-actions">
              <button className="btn btn--primary" disabled={saving === 'page'} onClick={handleSavePage} type="button">
                {saving === 'page' ? 'Saving...' : 'Save Page SEO'}
              </button>
              <button className="btn btn--secondary" disabled={saving === 'delete'} onClick={handleDeletePage} type="button">
                {saving === 'delete' ? 'Deleting...' : 'Delete Selected Override'}
              </button>
            </div>
          </div>

          <div className="admin-seo-previews">
            <div className="admin-seo-search-preview">
              <span>{previewUrl}</span>
              <strong>{previewTitle}</strong>
              <p>{previewDescription}</p>
            </div>
            <div className="admin-seo-social-preview">
              <div className="admin-seo-social-preview__media">{pageForm.openGraphImageUrl ? 'Image preview uses saved URL' : 'No image set'}</div>
              <strong>{pageForm.openGraphTitle || previewTitle}</strong>
              <p>{pageForm.openGraphDescription || previewDescription}</p>
              <span>{globalForm.siteName || 'FactorStreet'}</span>
            </div>
          </div>
        </div>
      </article>

      <div className="admin-seo-two-column">
        <article className="admin-panel admin-seo-panel">
          <p className="eyebrow">Sitemap Generator</p>
          <h2>Sitemap XML</h2>
          <div className="admin-seo-stats">
            <span>Sitemap URL</span><strong>https://factorstreet.com/sitemap.xml</strong>
            <span>Last generated</span><strong>{formatDate(settings?.sitemap?.lastGeneratedAt)}</strong>
            <span>URL count</span><strong>{settings?.sitemap?.urlCount || 0}</strong>
          </div>
          <button className="btn btn--primary" disabled={saving === 'sitemap'} onClick={handleRegenerateSitemap} type="button">
            {saving === 'sitemap' ? 'Regenerating...' : 'Regenerate Sitemap'}
          </button>
        </article>

        <article className="admin-panel admin-seo-panel">
          <p className="eyebrow">Robots.txt Control</p>
          <h2>Crawler rules</h2>
          <Field label="Allow paths"><textarea value={robotsForm.allowPathsText || ''} onChange={(event) => updateRobots('allowPathsText', event.target.value)} /></Field>
          <Field label="Disallow paths"><textarea value={robotsForm.disallowPathsText || ''} onChange={(event) => updateRobots('disallowPathsText', event.target.value)} /></Field>
          <Field label="Sitemap URL"><input value={robotsForm.sitemapUrl || ''} onChange={(event) => updateRobots('sitemapUrl', event.target.value)} /></Field>
          <pre className="admin-seo-robots-preview">{buildRobotsPreview(robotsForm)}</pre>
          <div className="admin-seo-actions">
            <button className="btn btn--primary" disabled={saving === 'robots'} onClick={handleSaveRobots} type="button">
              {saving === 'robots' ? 'Saving...' : 'Save Robots Settings'}
            </button>
            <button className="btn btn--secondary" disabled={saving === 'robots'} onClick={handleSaveRobots} type="button">
              Regenerate robots.txt
            </button>
          </div>
        </article>
      </div>

      <article className="admin-panel admin-seo-panel">
        <p className="eyebrow">SEO Quick Guide</p>
        <h2>What each control affects</h2>
        <div className="admin-seo-guide">
          {['Meta title', 'Meta description', 'Canonical URL', 'Open Graph', 'Sitemap', 'Robots.txt'].map((item) => (
            <div key={item}><strong>{item}</strong><span>{item === 'Robots.txt' ? 'Crawler permission hints.' : 'Public search and share metadata.'}</span></div>
          ))}
        </div>
      </article>
    </section>
  );
}

export default AdminSeo;
